import { PipelineNotificationType } from '@/components/Notifiers/typings/PipelineNotificationType';
import { getSmallDetailsValidatorColor } from '@/components/SmallDetails/utils/helper';
import { ProductPrintPositionBoxModel } from '@/typings';
import {
  constructArtworkValidationPipeline,
  maxNumberOfColoursValidator,
  resolutionValidator,
  ValidationResult,
  ValidatorResult,
} from '@/validators';
import { AnnotationShape } from '@/web-components/import-wizard/typings';
import { defineStore } from 'pinia';
import { useStore as useProductStore } from './product';
import { useStore as useShapeStore } from './shape';
import { LogoShape } from '@/web-components/import-wizard/typings/pintura';
import { NotificationSnapshot } from '@/typings/history';
import { isShapeColorChanged } from '@/utils/personalizationHelper';

export const useStore = defineStore({
  id: 'notification',

  state: () => {
    return {
      hasRedNotificationOnAnyPosition: false,
      notificationsForBell: [] as PipelineNotificationType[],
      allValidatorPipelineResults: [] as ValidatorResult[],
    };
  },
  getters: {
    redWarrnings: (state): boolean => {
      const hasRedErrors = state.notificationsForBell.some(
        (n) => n.validatorColor === 'Red'
      );
      return hasRedErrors;
    },
  },
  actions: {
    resetNotificationStoreStateToDefault() {
      this.$patch((state) => {
        state.hasRedNotificationOnAnyPosition = false;
        state.notificationsForBell = [] as PipelineNotificationType[];
        state.allValidatorPipelineResults = [] as ValidatorResult[];
      });
    },
    resetAllValidatorPipelineResults(positionId: string) {
      this.allValidatorPipelineResults =
        this.allValidatorPipelineResults.filter((v) => {
          return v.validationResult.find(
            (vr: any) => vr.printPositionId !== positionId
          );
        });

      this.resetNotificationsForBell(positionId);
    },
    findRedNotifications() {
      this.hasRedNotificationOnAnyPosition = this.notificationsForBell.some(
        (notification) => notification.validatorColor === 'Red'
      );
    },
    resetNotificationsForBell(printPositionId: string) {
      // only reset notifications for the current position
      this.notificationsForBell = this.notificationsForBell.filter(
        (n) => n.printPositionId !== printPositionId
      );
    },
    resetCommercialRemarksNotificationsForBell(printPositionId: string) {
      // only reset notifications for the current position
      this.notificationsForBell = this.notificationsForBell.filter(
        (n) =>
          n.printPositionId !== printPositionId ||
          (n.printPositionId === printPositionId &&
            n.validatorName !== 'commercialRemark')
      );
    },
    resetNotificationsForOneShape(relevantShapeId: string) {
      this.notificationsForBell = this.notificationsForBell.filter(
        (n) => n.payloadId !== relevantShapeId
      );
      this.allValidatorPipelineResults =
        this.allValidatorPipelineResults.filter(
          (n) => n.payload.id !== relevantShapeId
        );
    },
    setAllValidatorPipelineResults(results: ValidatorResult[]) {
      this.allValidatorPipelineResults =
        this.allValidatorPipelineResults.concat(results);
      this.setNotificationsForBell();
    },
    setCommercialRemarkNotification() {
      const productStore = useProductStore();

      const activePosition = productStore.activePosition;
      const printPostion = productStore.data.productPrintPositions?.find(
        (p) => p.id === activePosition.id
      );

      const technique = printPostion?.printTechniques.find((technique) => {
        return technique.id === productStore.selectedPrintPosition.techniqueId;
      });
      const commercialRemarks = technique?.commercialRemarksCodes
        ? [
            ...new Set(
              technique?.commercialRemarksCodes
                .concat(technique?.printTechnique.group.commercialRemarksCodes)
                .filter((value) => value !== null)
            ),
          ]
        : technique?.printTechnique.group.commercialRemarksCodes;

      if (commercialRemarks && commercialRemarks.length > 0) {
        commercialRemarks.forEach((remark: string) => {
          if (remark.trim() === 'ProofComment37' && !isShapeColorChanged()) {
            return;
          }
          if (
            productStore.activePosition.id &&
            !this.notificationsForBell.find(
              (notification) =>
                notification.printPositionId === activePosition.id &&
                notification.message === remark.trim()
            )
          ) {
            this.notificationsForBell = [
              ...this.notificationsForBell,
              {
                validatorColor: 'Orange',
                validatorName: 'commercialRemark',
                printPositionId: activePosition.id || '',
                printPosition: activePosition.name || '',
                message: remark.trim(),
                payloadId: activePosition.id || '',
                fileName: '',
              },
            ];
          }
        });
      }

      this.$patch((state) => {
        state.notificationsForBell = this.notificationsForBell;
      });
    },

    setNotificationsForBell() {
      const productStore = useProductStore();

      const remarks = this.notificationsForBell.filter(
        (not) => not.validatorName === 'commercialRemark'
      );

      this.notificationsForBell = [];
      this.allValidatorPipelineResults.forEach((result) => {
        result.validationResult.forEach((vr: any) => {
          if (
            productStore.printPositions.find((p) => p.id === vr.printPositionId)
          ) {
            if (
              vr.validatorName === 'smallDetails' &&
              !productStore.isSmallDetailsTechnique()
            )
              return;
            this.notificationsForBell.push({
              validatorColor: vr.validatorColor,
              validatorName: vr.validatorName,
              message: vr.message,
              payloadId: result.payload.id,
              fileName: result.payload.fileName || result.payload.text,
              printPosition:
                productStore.printPositions.find(
                  (p) => p.id === vr.printPositionId
                )?.name ?? '',
              printPositionId: vr.printPositionId,
            });
          }
        });
      });

      this.notificationsForBell = [...this.notificationsForBell, ...remarks];

      this.$patch((state) => {
        state.notificationsForBell = this.notificationsForBell;
      });

      this.setCommercialRemarkNotification();
      this.revalidateEmptyPrintPositions();
    },
    revalidateUnavailablePrintPositions() {
      const productStore = useProductStore();
      const shapeStore = useShapeStore();

      // This resets all the validator results
      this.allValidatorPipelineResults = [];
      this.notificationsForBell = [];

      const allResults = [] as ValidatorResult[];
      productStore.unavailablePrintPositions.forEach((up) => {
        const activePrintPosition =
          productStore.data.productPrintPositions.find(
            (pp) => pp.id === up.positionId
          );

        if (activePrintPosition) {
          const activePrintTechnique = activePrintPosition.printTechniques.find(
            (t) => t.id === up.technique.id
          );

          if (activePrintTechnique) {
            const selectedPrintPosition = {
              ...activePrintPosition,
              techniqueId: activePrintTechnique.id,
              techniqueName: activePrintTechnique.printTechnique.name,
              maximumColours: activePrintTechnique.maximumColours,
            } as ProductPrintPositionBoxModel;

            const relevantShapeMap = shapeStore.positionShapeMap.filter(
              (p) => p.id === selectedPrintPosition.id
            );

            const relevantShapes = shapeStore.shapes.filter((a) => {
              return relevantShapeMap.find((s) => s.logoId === a.id);
            });

            for (const shape of relevantShapes) {
              if (shape && shape.kind === 'Logo') {
                // Constructing the validation pipeline
                const pipeline = constructArtworkValidationPipeline(
                  productStore.data,
                  [selectedPrintPosition],
                  relevantShapes,
                  shape,
                  false
                );

                // Invoking the validation pipeline
                const pipelineResult = pipeline({
                  payload: shape,
                  validationResult: [],
                });

                // Add the new validation results for the specific artwork to the array
                allResults.push(pipelineResult);
              }
            }

            if (relevantShapes.length) {
              const validator = maxNumberOfColoursValidator(
                relevantShapes,
                selectedPrintPosition.maximumColours ?? 0,
                selectedPrintPosition.id
              );

              // Execute the max number of colours validator
              const result = validator({
                payload: { id: selectedPrintPosition.id },
                validationResult: [] as ValidationResult[],
              });

              allResults.push(result);
            }
          }
        }
      });
      this.setAllValidatorPipelineResults(allResults);
    },
    revalidateColorsOnly() {
      const productStore = useProductStore();
      const shapeStore = useShapeStore();

      const relevantShapeMap = shapeStore.positionShapeMap.filter(
        (p) => p.id === productStore.selectedPrintPosition.id
      );
      const relevantShapes = shapeStore.shapes.filter((s) => {
        return relevantShapeMap.find((sm) => sm.logoId === s.id);
      });

      // Reset all max number of colours validator results for the current print position
      this.allValidatorPipelineResults.forEach((result) => {
        result.validationResult = result.validationResult.filter((v: any) => {
          return (
            v.printPositionId !== productStore.selectedPrintPosition.id ||
            (v.printPositionId === productStore.selectedPrintPosition.id &&
              v.validatorName !== 'maxNumberOfColoursValidator')
          );
        });
      });

      this.allValidatorPipelineResults =
        this.allValidatorPipelineResults.filter((result) => {
          return result.validationResult.length !== 0;
        });

      this.resetNotificationsForBell(productStore.selectedPrintPosition.id);

      const results = [];

      /* Execute the max number of colours validator only once for all shapes
                payload is not needed */
      if (relevantShapes.length > 0) {
        const validator = maxNumberOfColoursValidator(
          relevantShapes,
          productStore.selectedPrintPosition.maximumColours ?? 0,
          productStore.selectedPrintPosition.id
        );

        const result = validator({
          payload: { id: productStore.selectedPrintPosition.id },
          validationResult: [] as ValidationResult[],
        });
        results.push(result);
      }
      this.setAllValidatorPipelineResults(results);
    },
    revalidateEmptyPrintPositions() {
      const productStore = useProductStore();
      const emptyPrintPositions = productStore.unavailablePrintPositions.filter(
        (p) => {
          const shapeStore = useShapeStore();
          const relevantShapeIds = shapeStore.positionShapeMap.filter(
            (psm) => psm.id === p.positionId
          );

          return relevantShapeIds.length === 0;
        }
      );

      if (emptyPrintPositions.length > 0) {
        emptyPrintPositions.forEach((p) => {
          if (
            !this.notificationsForBell.some(
              (n) =>
                n.printPositionId === p.positionId &&
                n.validatorName === 'emptyPrintPosition'
            ) &&
            p.positionId
          ) {
            this.notificationsForBell.push({
              validatorColor: 'Red',
              validatorName: 'emptyPrintPosition',
              payloadId: p.positionId,
              fileName: '',
              printPositionId: p.positionId,
              printPosition: p.positionName,
              message: '',
            });
          }
        });
      }
    },

    updateNotificationStateFromSnapshot(snapshot: NotificationSnapshot) {
      this.$patch((state) => {
        if (snapshot !== undefined) {
          state.notificationsForBell = snapshot.notificationsForBell;
          state.allValidatorPipelineResults =
            snapshot.allValidatorPipelineResults;
        }
      });
    },

    updateSmallDetailsNotifications() {
      const shapeStore = useShapeStore();
      shapeStore.shapes.map((shape) => {
        if (
          shape.smallDetailsResult &&
          shape.smallDetailsResult.status !== 'NoCheck'
        ) {
          const validationResult =
            this.allValidatorPipelineResults.find(
              (x) => x.payload.id === shape.id
            )?.validationResult ?? [];

          const smallDetails = validationResult.find(
            (x: {
              validatorName: string;
              printPositionId: string;
              validatorColor: string;
            }) => x.validatorName === 'smallDetails'
          );
          if (smallDetails) {
            smallDetails.validatorColor = getSmallDetailsValidatorColor(
              shape.smallDetailsResult.status
            );
          } else {
            const positionId = shapeStore.positionShapeMap.find(
              (x) => x.logoId === shape.id
            )?.id;
            const smallDetailsValidator = {
              printPositionId: positionId,
              validatorName: 'smallDetails',
              validatorColor: getSmallDetailsValidatorColor(
                shape.smallDetailsResult.status
              ),
            };
            this.allValidatorPipelineResults.push({
              payload: shape,
              validationResult: [...validationResult, smallDetailsValidator],
            });
          }
        }
      });

      this.setNotificationsForBell();
    },

    initializeSmallDetailsNotifications() {
      const shapeStore = useShapeStore();

      shapeStore.shapes.map((shape) => {
        if (
          shape.smallDetailsResult &&
          shape.smallDetailsResult.status !== 'NoCheck'
        ) {
          const shapeValidationIdx = this.allValidatorPipelineResults.findIndex(
            (x) => x.payload.id === shape.id
          );

          const positionId = shapeStore.positionShapeMap.find(
            (x) => x.logoId === shape.id
          )?.id;
          const smallDetailsValidator = {
            printPositionId: positionId,
            validatorName: 'smallDetails',
            validatorColor: getSmallDetailsValidatorColor(
              shape.smallDetailsResult.status
            ),
          };

          if (shapeValidationIdx !== -1) {
            this.allValidatorPipelineResults
              .find((x) => x.payload.id === shape.id)
              ?.validationResult.push(smallDetailsValidator);
          } else {
            this.allValidatorPipelineResults.push({
              payload: shape,
              validationResult: [smallDetailsValidator],
            });
          }
        }
      });
      this.setNotificationsForBell();
    },

    revalidateResolutionOnly(
      relevantShape: AnnotationShape,
      validatorConfiguration: string
    ) {
      const productStore = useProductStore();

      const validator = resolutionValidator(
        validatorConfiguration,
        relevantShape as LogoShape,
        productStore.selectedPrintPosition.id
      );

      const resultValidator = validator({
        payload: relevantShape,
        validationResult: [] as ValidationResult[],
      });

      const targetIdx = this.allValidatorPipelineResults.findIndex(
        (v) =>
          v.payload.id === relevantShape.id &&
          v.validationResult.findIndex(
            (r: any) => r.validatorName === 'resolutionValidator'
          ) !== -1
      );
      if (targetIdx !== -1) {
        const resolutionValIdx = this.allValidatorPipelineResults[
          targetIdx
        ].validationResult.findIndex(
          (r: any) => r.validatorName === 'resolutionValidator'
        );

        this.allValidatorPipelineResults[targetIdx].validationResult.splice(
          resolutionValIdx,
          1,
          resultValidator.validationResult[0]
        );

        this.resetNotificationsForBell(productStore.selectedPrintPosition.id);
        this.setNotificationsForBell();
      }
    },
  },
});
